import {action, makeObservable, observable, runInAction} from "mobx";

import {AdminApi} from "../../../api/api";
import {DocumentGroupInput} from "../../../api/values/document-group-input";
import {DocumentGroupOutput} from "../../../api/values/document-group-output";
import {Stores} from "../../../stores";
import {Permission} from "../../../stores/authentication-store";
import {SimpleToast} from "../../../stores/toast-store";
import {buildDeleteMessage} from "../../../utils/delete-message";
import {PageStoreConstructorOptions} from "../../../utils/page-store";
import {PageRouter} from "../../../utils/route-util";
import {validateLength} from "../../../utils/validation/functions/length";
import {validateSlug} from "../../../utils/validation/functions/slug";
import {ValidationResult, ValidationResultEmpty} from "../../../utils/validation/validation-result";
import {deleteDialogStore} from "../../organisms/delete-dialog/delete-dialog-store";

export class DocumentGroupEditPageStore {
    @observable
    public mediaId: string = "";

    @observable
    public key: string = "";

    @observable
    public loadingKey: string = "";

    @observable
    public title: string = "";

    @observable
    public initialTitle: string = "";

    @observable
    public slugs: string[] = [];

    @observable
    public titleValidationResult: ValidationResult = ValidationResultEmpty;

    @observable
    public keyValidationResult: ValidationResult = ValidationResultEmpty;

    @observable
    public canSave: boolean = false;

    @observable
    public isSaving: boolean = false;

    @observable
    public lackedSlugs: string[] = [];

    private adminApi: AdminApi;
    private stores: Stores;
    private pageRouter: PageRouter;

    constructor(options: PageStoreConstructorOptions) {
        makeObservable(this);

        this.adminApi = options.adminApi;
        this.stores = options.stores;
        this.pageRouter = options.pageRouter;
    }

    public async initialize({query}: any = {}) {
        this.stores.authenticationStore.checkAnyPermissionExists(Permission.EDIT_GENERAL_RESOURCES);

        const key = query && query.key;
        if (!key) {
            throw new Error("key is not set");
        }

        this.setMediaId(query.mediaId || "");

        await this.refresh(key);
    }

    @action.bound
    public setMediaId(s: string) {
        this.mediaId = s;
    }

    @action.bound
    public async setTitle(s: string) {
        this.title = s;
        const titleValidationResult = await this.validateTitle(s);
        runInAction(() => {
            this.titleValidationResult = titleValidationResult;
            this.updateCanSave();
        });
    }

    @action.bound
    public setSlugs(arr: string[]) {
        this.slugs = arr;
    }

    @action.bound
    public async refresh(key: string) {
        this.loadingKey = key;
        this.key = key;
        await this.load();
    }

    @action.bound
    public async saveAndRefresh() {
        this.isSaving = true;
        const slugsBefore = this.slugs.slice(0);
        const previousLoadingKey = this.loadingKey;
        const nextKey = this.key;

        await this.save();

        runInAction(() => {
            this.loadingKey = nextKey;
        });
        await this.load();

        runInAction(() => {
            this.lackedSlugs = slugsBefore.filter((slug) => !this.slugs.includes(slug));
            this.isSaving = false;
            this.updateCanSave();
        });

        if (previousLoadingKey !== nextKey) {
            this.pageRouter.pushRoute("/admin/[mediaId]/document-groups/[key]", {
                mediaId: this.mediaId,
                key: nextKey,
            });
        }

        this.stores.toastStore.setSimpleToast(SimpleToast.SAVED);
    }

    private async load() {
        try {
            const output = await this.adminApi.getDocumentGroup(
                {key: this.loadingKey},
                new DocumentGroupInput({mediaId: this.mediaId}),
            );

            const title = output.title || "";
            const key = output.key;
            const [titleValidationResult, keyValidationResult] = await Promise.all([
                this.validateTitle(title),
                this.validateKey(key),
            ]);

            runInAction(() => {
                this.key = key;
                this.loadingKey = key;
                this.title = title;
                this.initialTitle = output.title;
                this.slugs = output.slugs;
                this.titleValidationResult = titleValidationResult;
                this.keyValidationResult = keyValidationResult;
                this.updateCanSave();
            });
        } catch (e) {
            // eslint-disable-next-line
            console.error(e);
            alert(e); // TODO: error message
        }
    }

    private async save() {
        try {
            await this.adminApi.putDocumentGroup(
                {mediaId: this.mediaId},
                {key: this.loadingKey},
                new DocumentGroupOutput({
                    key: this.key,
                    slugs: this.slugs,
                    title: this.title,
                }),
            );
        } catch (e) {
            // eslint-disable-next-line
            console.error(e);
            alert("保存できませんでした。" + e); // TODO: error message
        }
    }

    private createValidationResult(items: ValidationResult["items"]) {
        const result = new ValidationResult();
        result.run((r) => {
            r.items = items;
        });
        return result;
    }

    private async validateTitle(title: string) {
        const lengthErrors = await Promise.resolve(validateLength(undefined, 50)(title));
        return this.createValidationResult(lengthErrors);
    }

    private async validateKey(key: string) {
        const [lengthErrors, slugErrors] = await Promise.all([
            Promise.resolve(validateLength(1, 50)(key)),
            Promise.resolve(validateSlug()(key)),
        ]);
        return this.createValidationResult([...lengthErrors, ...slugErrors]);
    }

    public confirmDeleteDocumentGroup() {
        const message = buildDeleteMessage(this.title || this.key, false);
        deleteDialogStore.open(
            "ドキュメントグループ削除",
            message,
            async () => await this.deleteDocumentGroup(),
        );
    }

    private async deleteDocumentGroup() {
        try {
            await this.adminApi.deleteDocumentGroup(
                {mediaId: this.mediaId},
                {key: this.loadingKey},
            );
            this.stores.toastStore.setSimpleToast(SimpleToast.DELETED);
            this.pageRouter.pushRoute("/admin/[mediaId]/document-groups", {mediaId: this.mediaId});
        } catch (e) {
            // eslint-disable-next-line
            console.error(e);
            alert("削除できませんでした。" + e); // TODO: error message
        }
    }

    private updateCanSave() {
        this.canSave = this.titleValidationResult.isEmpty;
    }
}
